import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import Container from "@material-ui/core/Container";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import { toast } from 'react-toastify';
import { useHistory } from "react-router-dom";
import StyledCard from 'components/StyledCard';
import styled from "styled-components";
import PageLayout from "components/PageLayout";
import BackButton from "components/BackButton";
import TextField from '@material-ui/core/TextField';
import DayJS from 'react-dayjs';
import useAuth from "../../hooks/useAuth";
import useDb from "../../hooks/useDb";
import useFormDataApi from "../../hooks/useFormDataAPI"
import { useForm } from 'react-hook-form';
import useAppointment from "../../hooks/useAppointment";
import useAPI from "hooks/useAPI";
import useStyles from './styles';
import Brightness1Icon from '@material-ui/icons/Brightness1';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import { useAlert } from "../../providers/AlertService";
import useGlobalLoading from "hooks/useGlobalLoading"
import Compressor from "compressorjs";

const DeleteButton = styled.button`
  position: absolute;
  color: red;
  font-weight: bold;
  font-size: 1.5rem;
  top: 10px;
  right: 10px;
`;

const Picture = styled.img`
  width: 100%;
  height: 100%;
`;

const formsMetadata = [
  { name: 'wxwo', label: 'Weatherization Work Order', version: process.env.REACT_APP_WXWO_VERSION }, 
  { name: 'duct', label: 'Prescriptive Duct Sealing Work Order', version: process.env.REACT_APP_DUCT_VERSION }, 
  { name: 'ngat', label: 'NGAT', version: process.env.REACT_APP_NGAT_VERSION }, 
  { name: 'noc', label: 'Notice of Unsatisfactory Condition', version: process.env.REACT_APP_NOC_VERSION }, 
  { name: 'aiwo', label: 'Attic Insulation Work Order Form', version: process.env.REACT_APP_ATTIC_VERSION }, 
  { name: 'washer', label: 'Washer Assessment/Installation Work Order', version: process.env.REACT_APP_WASHER_VERSION },
  { name: 'sce', label: 'SCE Assessment', version: process.env.REACT_APP_SCE_VERSION }
]

const stateFormsMetadata = [
  { name: 'casInterim', label: 'CAS Interim', multi: false, version: process.env.REACT_APP_NGAT_VERSION },
  { name: 'noticeOfSurvey', label: 'Notice of Survey', multi: false, version: process.env.REACT_APP_NGAT_VERSION },
  { name: 'windowAndDoor', label: 'Window and Door', multi: false, version: process.env.REACT_APP_NGAT_VERSION },
  { name: 'insulationCertificate', label: 'Insulation Certificate', multi: false, version: process.env.REACT_APP_NGAT_VERSION },
  { name: 'casRAndR', label: 'CAS Repair & Replace', multi: true, hub: `/form/repaired-and-replaced`, version: process.env.REACT_APP_NGAT_VERSION },
  { name: 'casinspection', label: 'CAS 700', multi: true, hub: `/form/casinspection`, version: process.env.REACT_APP_NGAT_VERSION },
  { name: 'casComplianceReport', label: "Compliance Report", multi: true, hub: `/form/compliance-report`, version: process.env.REACT_APP_NGAT_VERSION },
  { name: 'ductLeakage', label: "Duct Leakage", multi: true, hub: `/form/duct-leakage`, version: process.env.REACT_APP_NGAT_VERSION },
  { name: 'shellLeakage', label: "Shell Leakage", multi: true, hub: `/form/shell-leakage`, version: process.env.REACT_APP_NGAT_VERSION },
]

const Appointment = () => {
  const { register, handleSubmit, control } = useForm();
  const { setIsLoading } = useGlobalLoading();
  const formDataApi = useFormDataApi();
  const imgRef = useRef();
  const db = useDb();
  const params = useParams();
  const history = useHistory();
  const { appointmentId } = params;
  const { appointment, setAppointment } = useAppointment(appointmentId);
  const [forms, setForms] = useState([]);
  const [stateForms, setStateForms] = useState([]);
  const styles = useStyles();
  const instance = useAPI();
  const alert = useAlert();

  const submitEmptyAppointment = reasonForReschedule => {
    setIsLoading(true);

    const data = new FormData();
    data.append('reasonForReschedule', reasonForReschedule.rescheduleReason)
    console.log(data);

    formDataApi
      .post(`/appointments/${appointment._id}/finalize`, data)
      .then(res => {
        toast.success('Successfully submitted images!');
        setAppointment({
          ...appointment,
          completed: true
        });

        history.push('/');
      })
      .catch(err => {
        toast.error(err.message)
      })
      .finally(() => setIsLoading(false));
  }

  const handleAddPicture = async (event) => {
    event.preventDefault();
    try {

      const [picture] = imgRef.current.files;
      const buffer = await picture.arrayBuffer();
      const blob = new Blob([buffer]);

      const a = { ...appointment };
      a.pictures.push({ blob, name: picture.name });

      setAppointment(a);
    }
    catch (err) {
      console.log(err)
    }
  };

  const handleRemovePicture = (pictureIdx) => {
    if (window.confirm('Are you sure you want to delete the data for this form?')) {
      setAppointment({
        ...appointment,
        pictures: [
          ...appointment.pictures.slice(0, pictureIdx),
          ...appointment.pictures.slice(pictureIdx + 1),
        ],
      });
    }
  };

  const submitAppointment = async () => {
    const currentDateObj = new Date();
    const dateSubmitted = currentDateObj.toISOString()
    const initializedForms = await db.forms.where({ appointmentId }).toArray();
    const unreviewedForms = await db.forms.where({ appointmentId, reviewed: 0 }).toArray();

    const data = new FormData();

    const customerFullName = `${appointment.customer.firstName}${!!(appointment.customer.middleName) ? ` ${appointment.customer.middleName}` : ''} ${appointment.customer.lastName}`
    data.append('dateSubmitted', dateSubmitted);
    data.append('customerName', customerFullName);
    data.append('submissions', JSON.stringify([]));
    data.append('enrollmentNumber', appointment.customer.enrollment.enrollmentNumber);

    for (const picture of appointment.pictures) {
      console.log('added picture')
      data.append('images', picture.blob, picture.name)
    }

    if (!initializedForms.length) {
      if (appointment.pictures.length > 0) {
        if (!window.confirm("Are you sure to submit forms with pictures only?")) {
          return alert({ description: 'Must complete at least one form' });
        }
      } else return alert({ description: 'Must complete at least one form' });
    }

    if (unreviewedForms.length) {
      console.log('test')
      console.log({unreviewedForms})
      alert({ description: `The form(s) [${unreviewedForms.map(form => form.type).join(', ')}] were started but not reviewed. Either review them or delete them before submitting.` });
    }

    const submissions = initializedForms.map(form => ({
      name: form.type,
      currentRevision: null,
      version: form.version,
      enrollmentNumber: appointment.customer.enrollment.enrollmentNumber,
      fields: form
    }));

    console.log(submissions)

    setIsLoading(true)

    if (appointment.pictures.length > 0) {
      formDataApi.post(
        `/appointments/${appointment._id}/finalize`,
        data
      )
        .then(res => {
          toast.success('Successfully submitted images!');
          setAppointment({
            ...appointment,
            completed: true
          });

          history.push('/');
        })
        .catch(err => {
          toast.error(err.message)
        })
        .finally(() => setIsLoading(false));

      console.log(data)
    }

    if (initializedForms.length) {
      data.delete('submissions');
      data.append('submissions', JSON.stringify(submissions));
      data.delete('images');

      setIsLoading(true)
      formDataApi.post(
        `/appointments/${appointment._id}/finalize`,
        data
      )
        .then(res => {
          toast.success('Successfully finalized appointment!');
          setAppointment({
            ...appointment,
            completed: true
          });

          history.push('/');
        })
        .catch(err => {
          toast.error(err.message)
        })
        .finally(() => setIsLoading(false));
    }
  };

  useEffect(() => {
    Promise.all(formsMetadata.map(async metadata => {
      const result = await db.forms.get({ appointmentId, type: metadata.name, version: metadata.version });

      return {
        ...metadata,
        reviewed: result?.reviewed,
      }
    }))
      .then(res => {
        console.log(res)
        setForms(res)
      });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // useEffect(() => {
  //   Promise.all((async () => {
  //     const result = await db.forms.get({ appointmentId, type: "casInterim", version: 1 });

  //     return {
  //       reviewed: result?.reviewed
  //     }
  //   }))
  //     .then(res => {
  //       console.log(res)
  //       // setStateForms(res)
  //     });
  //   //eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [])

  useEffect(() => {
    Promise.all(stateFormsMetadata.map(async metadata => {
      const result = await db.forms.get({ appointmentId, type: metadata.name, version: metadata.version });

      return {
        ...metadata,
        reviewed: result?.reviewed,
      }
    }))
      .then(res => {
        console.log({res})
        setStateForms(res)
      });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const removeForm = (form) => {
    const result = window.confirm('Are you sure you want to delete the data for this form?');
    if (result === true)
      db.forms
        .where({
          appointmentId,
          type: form.name,
          version: form.version
        })
        .delete()
        .then(setForms(forms => forms.map(f => ({ ...f, reviewed: f.name === form.name ? undefined : f.reviewed }))))
  }

  if (!appointment) return null;

  return (
    <PageLayout title="Dashboard">
      <Container maxWidth="sm">
        <>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <h2>Customer Information</h2>
            <Box display="flex">
              <BackButton url='/' />
              <Box ml={1}>
                <Button variant="contained" onClick={submitAppointment} className={styles.submitButton} disabled={appointment.completed}>Submit</Button>
              </Box>
            </Box>
          </Box>
          <StyledCard>
            <Box display="flex" mb={2}>
              <Box flex={1}>
                <h3 className={styles.subheading}>Address:</h3>
                <p className={styles.paragraph}>{appointment.customer.firstName} {appointment.customer.lastName}</p>
                <p className={styles.paragraph}>{appointment.customer.streetNumber} {appointment.customer.streetName}{" "} {appointment.customer.unit ? `UNIT ${appointment.customer.unit}` : ''}</p>
                <p className={styles.paragraph}>{appointment.customer.city}, CA {appointment.customer.zip} {appointment.customer.primaryPhone}</p>
                <p className={styles.paragraph}>ENROLLMENT #: {appointment.customer.enrollment?.enrollmentNumber}</p>
                <p className={styles.paragraph} variant="body2">CLIMATE ZONE: {appointment.customer.climateZone}</p>
                <p className={styles.paragraph} variant="body2">OCCUPANCY STATUS: {appointment.customer.occupancyStatus}</p>
              </Box>
              <Box flex={1}>
                <h3 className={styles.subheading}>Time and Date:</h3>
                <p className={styles.paragraph}><DayJS format="MM/DD/YYYY">{appointment.start}</DayJS></p>
                <p className={styles.paragraph}><DayJS format="hh:mm a">{appointment.start}</DayJS> ~ <DayJS format="hh:mm a">{appointment.end}</DayJS></p>
              </Box>
            </Box>
            <Box>
              <h3 className={styles.subheading}>Description:</h3>
              <p className={styles.paragraph}>{appointment.description}</p>
            </Box>
          </StyledCard>
          <h2>Forms:</h2>
          <Box>
            {forms.map(form => {
              return <div key={form.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Link to={`/form/${form.name}/${form.version}/${appointment._id}`} className={styles.formButton}>{form.label}</Link>
                <Brightness1Icon
                  style={{
                    color: form.reviewed === undefined
                      ? 'white'
                      : form.reviewed === 0
                        ? 'yellow'
                        : 'green'
                  }}
                />
                {form.reviewed !== undefined &&
                  <DeleteForeverIcon
                    id={`${form.name}-trash`}
                    onClick={() => removeForm(form)}
                  />}
              </div>
            })}
          </Box>

          <h2>State Forms:</h2>
          <Box>
            {stateForms.map(stateForm => {
              if (!stateForm.multi) {
                console.log(stateForm.name, stateForm.reviewed)
                return <div key={stateForm.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {/* the "1" here represents the first page of that form, since some of these forms are multi-page */}
                  <Link onClick={() => console.log(`/form/${stateForm.name}/${stateForm.version}/${appointment._id}`)} to={`/form/${stateForm.name}/${stateForm.version}/${appointment._id}`} className={styles.formButton}>{stateForm.label}</Link>
                  <Brightness1Icon
                    style={{
                      color: stateForm.reviewed === undefined
                        ? 'white'
                        : stateForm.reviewed === 0
                          ? 'yellow'
                          : 'green'
                    }}
                  />
                  {stateForm.reviewed !== undefined &&
                    <DeleteForeverIcon
                      id={`${stateForm.name}-trash`}
                      onClick={() => removeForm(stateForm)}
                    />}
                </div>
              }
              else {
                console.log({"isMulti?": stateForm.multi })
                return <div key={stateForm.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {/* the "1" here represents the first page of that form, since some of these forms are multi-page */}
                  <Link onClick={() => console.log(`${stateForm.hub}/${appointmentId}`)} to={`${stateForm.hub}/${appointmentId}`} className={styles.formButton}>{stateForm.label}</Link>
                  <Brightness1Icon
                    style={{
                      color: stateForm.reviewed === undefined
                        ? 'white'
                        : stateForm.reviewed === 0
                          ? 'yellow'
                          : 'green'
                    }}
                  />
                  {/* {stateForm.reviewed !== undefined &&
                    <DeleteForeverIcon
                      id={`${stateForm.name}-trash`}
                      onClick={() => removeForm(stateForm)}
                    />} */}
                </div>
              }
            })}
          </Box>

          <h2>Pictures:</h2>
          <input type="file" ref={imgRef} accept="image/*;capture=camera" onChange={handleAddPicture} />
          {appointment.pictures.map((picture, idx) => {
            if (picture.blob)
              return (
                <div style={{ position: "relative" }}>
                  <DeleteButton onClick={() => handleRemovePicture(idx)}>
                    X
                  </DeleteButton>
                  <Picture
                    key={idx}
                    src={URL.createObjectURL(picture.blob)}
                    alt="attached to appointment"
                  />
                </div>
              );
            else
              return null;
          })}
          <p></p>
        </>
        <form onSubmit={handleSubmit(submitEmptyAppointment)}>
          <Box ml={1}>
            <Button type='submit' variant="contained" onClick={handleSubmit} className={styles.submitButton} disabled={appointment.completed}>Submit for later</Button>
          </Box>
          <p>State reason for submitting without forms:</p>
          <TextField name="rescheduleReason" placeholder="Comment" variant="outlined" inputRef={register} />
        </form>
      </Container>
    </PageLayout>
  );
};

const Page = () => {
  const { PrivateContent } = useAuth();

  return (
    <PrivateContent title="Appointment">
      <Appointment />
    </PrivateContent>
  );
};

export default Page;
